import { useEffect, useState } from 'react'
import { X, Users } from 'lucide-react'
import { getEventSubscriptions } from '@/services/calendar'
import LoadingSpinner from '@/components/ui/LoadingSpinner'

type Subscriber = {
  id: string
  userId?: string
  userType?: string
  notificationTypes?: string[]
  reminderMinutes?: number[]
  preferences?: Record<string, any>
  createdAt?: string
  user?: {
    id: string
    name?: string
    email?: string
    phoneNumber?: string
  }
}

interface SubscribersModalProps {
  isOpen: boolean
  onClose: () => void
  eventId: string
  eventTitle: string
}

export default function SubscribersModal({
  isOpen,
  onClose,
  eventId,
  eventTitle,
}: SubscribersModalProps) {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [loading, setLoading] = useState(false)
  const [subscribers, setSubscribers] = useState<Subscriber[]>([])
  const [total, setTotal] = useState<number | null>(null)
  const [totalPages, setTotalPages] = useState(1)

  const fetchSubscribers = async () => {
    setLoading(true)
    try {
      const res = await getEventSubscriptions(eventId, {
        page,
        limit: pageSize,
      })
      // Handle different response formats
      const data = res?.data || res?.items || res || []
      setSubscribers(Array.isArray(data) ? data : [])
      const meta = res?.meta
      if (meta?.total != null) {
        setTotal(meta.total)
        setTotalPages(Math.ceil(meta.total / pageSize))
      } else if (res?.total != null) {
        setTotal(res.total)
        setTotalPages(res.totalPages || Math.ceil(res.total / pageSize))
      }
    } catch (err) {
      console.error('Failed to fetch subscribers:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (isOpen) {
      fetchSubscribers()
    }
  }, [eventId, page, pageSize, isOpen])

  if (!isOpen) return null

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-blue-500 rounded-lg flex items-center justify-center">
              <Users className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Event Subscribers</h2>
              <p className="text-sm text-gray-500">{eventTitle}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <LoadingSpinner size="lg" />
            </div>
          ) : subscribers.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No subscribers yet</h3>
              <p className="text-gray-500">
                No users have subscribed to this event.
              </p>
            </div>
          ) : (
            <>
              <ul className="divide-y divide-gray-200 md:hidden">
                {subscribers.map((subscriber) => (
                  <li key={subscriber.id} className="py-3 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {subscriber.user?.name || subscriber.userId || '-'}
                        </p>
                        {subscriber.user?.email && (
                          <p className="text-xs text-gray-500 truncate">{subscriber.user.email}</p>
                        )}
                      </div>
                      <span className="shrink-0 inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-100 text-gray-800 capitalize">
                        {subscriber.userType || '-'}
                      </span>
                    </div>

                    {subscriber.notificationTypes && subscriber.notificationTypes.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {subscriber.notificationTypes.map((type, idx) => (
                          <span
                            key={idx}
                            className="inline-flex px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-800"
                          >
                            {type}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      {subscriber.reminderMinutes && subscriber.reminderMinutes.length > 0 && (
                        <span>
                          Reminders:{' '}
                          {subscriber.reminderMinutes
                            .map((mins) => (mins >= 60 ? `${Math.floor(mins / 60)}h` : `${mins}m`))
                            .join(', ')}
                        </span>
                      )}
                      <span>{formatDate(subscriber.createdAt)}</span>
                    </div>
                  </li>
                ))}
              </ul>

              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User Type
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Notification Types
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Reminders
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Subscribed At
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {subscribers.map((subscriber) => (
                      <tr key={subscriber.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {subscriber.user?.name || subscriber.userId || '-'}
                          </div>
                          {subscriber.user?.email && (
                            <div className="text-sm text-gray-500">
                              {subscriber.user.email}
                            </div>
                          )}
                          {subscriber.user?.phoneNumber && (
                            <div className="text-sm text-gray-500">
                              {subscriber.user.phoneNumber}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800 capitalize">
                            {subscriber.userType || '-'}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex flex-wrap gap-1">
                            {subscriber.notificationTypes && subscriber.notificationTypes.length > 0 ? (
                              subscriber.notificationTypes.map((type, idx) => (
                                <span
                                  key={idx}
                                  className="inline-flex px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-800"
                                >
                                  {type}
                                </span>
                              ))
                            ) : (
                              <span className="text-gray-400 text-sm">-</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {subscriber.reminderMinutes && subscriber.reminderMinutes.length > 0 ? (
                              subscriber.reminderMinutes.map((mins, idx) => (
                                <span key={idx} className="mr-2">
                                  {mins >= 60 ? `${Math.floor(mins / 60)}h` : `${mins}m`}
                                  {idx < subscriber.reminderMinutes!.length - 1 && ', '}
                                </span>
                              ))
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(subscriber.createdAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {total !== null && total > pageSize && (
                <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-4">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-700">Show</span>
                    <select
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value))
                        setPage(1)
                      }}
                      className="border border-gray-300 rounded px-2 py-1 text-sm"
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                    <span className="text-sm text-gray-700">per page</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600">
                      {Math.min((page - 1) * pageSize + 1, total)} -{' '}
                      {Math.min(page * pageSize, total)} of {total}
                    </span>
                    <div className="flex space-x-1">
                      <button
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page === 1}
                        className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setPage((p) => p + 1)}
                        disabled={page >= totalPages}
                        className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Modal Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
